import { useState, type FormEvent } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { authErrorKey, confirmForgotPassword } from "@/auth/cognito/client";

import { AuthLayout } from "./AuthLayout";
import { Field, FormError, PasswordField, SubmitButton } from "./fields";

/**
 * Step two of a password reset: the emailed code plus the new password.
 *
 * <p>Ends at the sign-in page rather than signing the user in directly —
 * {@code confirmPassword} returns no session, and asking them to use the
 * password they just chose is the honest confirmation that it took.</p>
 */
export function ResetPasswordPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const emailParam = params.get("email") ?? "";

  const [email, setEmail] = useState(emailParam);
  const [code, setCode] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await confirmForgotPassword(email, code, password);
      navigate(`/signin?reset=1&email=${encodeURIComponent(email.trim())}`, { replace: true });
    } catch (err) {
      setError(t(authErrorKey((err as { code?: string }).code ?? "UnknownError")));
    } finally {
      setBusy(false);
    }
  }

  return (
    <AuthLayout
      title={t("auth.reset.title")}
      subtitle={t("auth.reset.subtitle", { email: email || t("auth.confirm.yourEmail") })}
      footer={
        <Link to="/signin" className="font-medium text-teal-700 hover:text-teal-800">
          {t("auth.backToSignIn")}
        </Link>
      }
    >
      <form className="space-y-4" onSubmit={onSubmit}>
        {!emailParam && (
          <Field
            label={t("auth.fields.email")}
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="email"
            required
          />
        )}
        <Field
          label={t("auth.fields.code")}
          value={code}
          onChange={(e) => setCode(e.target.value)}
          inputMode="numeric"
          autoComplete="one-time-code"
          autoFocus
          required
        />
        <PasswordField
          label={t("auth.fields.newPassword")}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          autoComplete="new-password"
          required
          minLength={8}
        />
        <p className="text-xs text-slate-500">{t("auth.passwordRules")}</p>
        <FormError>{error}</FormError>
        <SubmitButton busy={busy}>{t("auth.reset.submit")}</SubmitButton>
      </form>
    </AuthLayout>
  );
}
